import PropTypes from 'prop-types';
import {Layer, measureText} from "./Canvas.jsx";

import deepEqual from "lib/deepEqual";
import getDefaultFont from "lib/getDefaultFont";
import {multiFormatter} from "../lib/TimeUtils";

const truncate = txt => txt.length > 25 ? txt.slice(0, 22) + "..." : txt;

const shouldHide = (ticks, axisWidth) => ticks.length * 20 > axisWidth;

const textOverlapps = (ticks, axisWidth) => {
    let maxConsecutive = 0, prev = 0;
    let widths = ticks
        .map(txt => measureText(txt, {size: TICK_TEXT_SIZE, fontFamily: getDefaultFont()}));

    for (let w of widths) {
        maxConsecutive = Math.max(maxConsecutive, prev + w);
        prev = w;
    }

    return ticks.length * (10 + maxConsecutive) / 2 > axisWidth;
};


const getYTickCount = height => Math.ceil(height / 50);

export class ValueLines extends Layer {
    static propTypes = {
        scale: PropTypes.func
    };

    static displayName = 'ValueLines';

    doDraw() {
        let {width, height} = this.getSize();
        let ctx = this.getCtx();

        ctx.setLineDash([2, 1]);
        ctx.strokeStyle = "#dcdcdc";

        let scale = this.props.scale;

        let y = value => Math.round(scale(value) * height);

        for (let tick of scale.ticks(getYTickCount(height))) {
            if (tick == 0) {
                continue;
            }
            ctx.beginPath();
            ctx.moveTo(0, y(tick));
            ctx.lineTo(width, y(tick));
            ctx.stroke();
        }
    }


    componentWillReceiveProps(nextProps) {
        let oldScale = this.props.scale;
        let nextScale = nextProps.scale;

        if (!deepEqual(oldScale.ticks(), nextScale.ticks())) {
            this.markDirty();
        }
    }
}

const getTickFormatter = scale => {
    let ticks = scale.ticks ? scale.ticks() : scale.domain();

    if (ticks.some(d => d instanceof Date === false)) {
        return d => String(d);
    }

    let first = 0;

    let hasMinutes = ticks.some(d => d.getUTCMinutes()),
        hasHours = hasMinutes || ticks.some(d => d.getUTCHours()),
        hasMultipleDays = ticks.some(d => ticks[0].getUTCDate() !== d.getUTCDate()),
        hasDays = ticks.some(d => d.getUTCDate() !== 1);

    return multiFormatter([
        ["%H:%M\n%Y-%m-%d", function(d) {
            return hasHours && !hasMultipleDays && first++ === 0;
        }],
        ["%H:%M", function(d) {
            return d.getUTCMinutes() || d.getUTCHours();
        }],
        ["%Y-%m-%d", function(d) {
            return hasDays || d.getUTCDate() !== 1;
        }],
        ["%Y-%m", function(d) {
            return d.getUTCMonth() || d.getUTCFullYear();
        }],
        ["Invalid time", function() {
            return true;
        }]
    ]);
};

const TICK_TEXT_SIZE = 13;
const ROTATE_ANGLE = -Math.PI / 3;

class AxisBase extends Layer {
    static propTypes = {
        name: PropTypes.string,
        scale: PropTypes.func,
        values: PropTypes.array,
        size: PropTypes.number
    };

    componentWillReceiveProps(nextProps) {

        let oldScale = this.props.scale;
        let nextScale = nextProps.scale;

        if (["scale", "domain"].some(fn => typeof nextScale[fn] !== typeof oldScale[fn])) {
            this.markDirty();
        } else if ([nextScale, oldScale].every(scale => typeof scale.ticks === "function")) {
            if (!deepEqual(oldScale.ticks(), nextScale.ticks())) {
                this.markDirty();
            }
        } else {
            if (!deepEqual(oldScale.domain(), nextScale.domain())) {
                this.markDirty();
            }
        }

        if (["name", "leftMargin", "rightMargin", "type", "estimatedWidth"].some(key => nextProps[key] !== this.props[key])) {
            this.markDirty();
        }
    }

    doDraw() {
        let ctx = this.getCtx();
        ctx.beginPath();

        ctx.strokeStyle = "black";
        ctx.fillStyle = "black";
        ctx.font = `normal ${TICK_TEXT_SIZE}px ${getDefaultFont()}`;
        ctx.textBaseline = 'middle';
    }
}

export class XAxis extends AxisBase {
    static displayName = 'XAxis';

    getEstimatedSize() {
        let {scale, values, estimatedWidth, name} = this.props;

        let txtheight = 0;
        if (typeof scale.ticks !== "function") {
            if (shouldHide(values, estimatedWidth)) {
                txtheight = 0;
            }else{
                values = values
                    .map(String)
                    .map(truncate);

                if (textOverlapps(values, estimatedWidth)){
                    let widths = values
                        .map(txt => measureText(txt, {size: TICK_TEXT_SIZE}));

                    let maxWidth = Math.max(...widths);
                    txtheight = TICK_TEXT_SIZE * 1.5 * Math.abs(Math.cos(ROTATE_ANGLE))
                        + maxWidth * Math.abs(Math.sin(ROTATE_ANGLE));
                }else{
                    txtheight = TICK_TEXT_SIZE * 1.5;
                }
            }
        }else{
            txtheight = values && values.length > 0? 25 : 0;
        }

        let nameheight = name && name.length > 0 ? 25 : 0;

        return {height: Math.ceil(nameheight + txtheight)};
    }

    doDraw() {
        super.doDraw();
        let {width, height} = this.getSize();
        let {scale, values, estimatedWidth} = this.props;
        let ctx = this.getCtx();

        // main axis line
        ctx.lineWidth = 1;

        let leftMargin = this.props.leftMargin || 0;
        let rightMargin = this.props.rightMargin || 0;

        width = width - rightMargin - leftMargin;

        ctx.beginPath();
        ctx.moveTo(leftMargin, 6);
        ctx.lineTo(leftMargin, 0.5);
        ctx.lineTo(width + leftMargin, 0.5);
        ctx.lineTo(width + leftMargin, 6);
        ctx.stroke();

        let tickCount = Math.min(Math.ceil(width / 70), values.length),
            ticks,
            formatter,
            rotate = false;

        if (values.length === 0) {
            ticks = [];
        } else if (typeof scale.ticks !== "function") {
            if (shouldHide(values, estimatedWidth)) {
                ticks = [];
            } else {
                formatter = val => String(val);

                ticks = values;

                if (textOverlapps(ticks.map(formatter), estimatedWidth)) {
                    formatter = val => truncate(String(val));
                }

                if (textOverlapps(ticks.map(formatter), estimatedWidth)) {
                    rotate = true;
                }
            }
        } else {
            for (; ;) {
                ticks = scale
                    .ticks(tickCount);

                let format = getTickFormatter(scale);

                let widths = ticks.map(tick => ctx.measureText(format(tick).split("\n")[0]).width);

                let maxConsecutive = 0, prev = 0;
                for (let width of widths) {
                    maxConsecutive = Math.max(maxConsecutive, prev + width);
                    prev = width;
                }

                if (ticks.length * (10 + maxConsecutive) / 2 < width || tickCount <= 1) {
                    break;
                } else {
                    tickCount--;
                }
            }

            formatter = getTickFormatter(scale);
        }


        let x = value => Math.round(width * scale(value) + leftMargin);

        ctx.beginPath();
        //draw axis ticks

        ctx.font = `normal ${TICK_TEXT_SIZE}px ${getDefaultFont()}`;

        for (let tick of ticks) {
            let xPos = x(tick);
            if (typeof scale.ticks !== "function" && typeof scale.bandwidth == "function") {
                xPos += scale.bandwidth() * width / 2;
            }

            ctx.beginPath();
            ctx.moveTo(xPos, 0);
            ctx.lineTo(xPos, 6);
            ctx.stroke();

            let txt = formatter(tick);

            ctx.textAlign = "center";
            ctx.textBaseline = "hanging";
            let txtRows = txt.split("\n");
            for (let i = 0; i < txtRows.length; i++) {
                ctx.save();
                ctx.translate(xPos, 10 + i * (TICK_TEXT_SIZE + 4));

                if (rotate) {
                    ctx.textAlign = "end";
                    ctx.rotate(ROTATE_ANGLE);
                }

                ctx.fillText(txtRows[i], 0, 0);
                ctx.restore();
            }
        }

        // key name
        ctx.beginPath();
        ctx.font = `normal ${TICK_TEXT_SIZE + 2}px ${getDefaultFont()}`;
        ctx.textAlign = "center";
        ctx.textBaseline = 'bottom';
        ctx.fillText(this.props.name, width / 2 + leftMargin, height);
    }
}

export class YAxis extends AxisBase {
    static displayName = 'YAxis';

    static estimateWidth(scale) {
        let format = scale.tickFormat();
        let ticks = scale.ticks();
        let last = ticks[ticks.length - 1];
        let width = last !== void 0 ? measureText(format(last), {size: TICK_TEXT_SIZE}) : 0;
        return Math.ceil(width + 30);
    }

    getEstimatedSize() {
        return {width: YAxis.estimateWidth(this.props.scale)};
    }

    doDraw() {
        super.doDraw();

        let {width, height} = this.getSize();
        let {scale} = this.props;
        let ctx = this.getCtx();

        let y = value => Math.round(height * scale(value));

        let format = scale.tickFormat();
        ctx.textAlign = "right";
        for (let tick of scale.ticks(getYTickCount(height))) {
            if (tick === 0) {
                continue;
            }
            ctx.beginPath();
            ctx.fillText(format(tick), width - 10, y(tick));
        }

        // rotated key name
        ctx.save();
        ctx.translate(0, height / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.font = `normal ${TICK_TEXT_SIZE + 2}px ${getDefaultFont()}`;

        ctx.textAlign = "center";
        ctx.textBaseline = 'hanging';

        ctx.fillText(this.props.name, 0, 0);
        ctx.restore();
        ctx.closePath();
    }
}


// WEBPACK FOOTER //
// ./src/kpi/charts/Axis.jsx